import { useEffect, useRef, useState } from 'react';

import { SELECT_ANNOTATIONS_IN_RECT } from '../actions/selectAnnotationsInRect';
import { NODES_TRANSFORMER_ID } from '../utils/constants';
import {
  isCanvasPanMode,
  isCanvasSelectMode,
} from '../utils/canvasInteractionMode';
import getAnnotationIdsInMarqueeRect from '../utils/getAnnotationIdsInMarqueeRect';
import {
  MIN_MARQUEE_SELECTION_SIZE,
  normalizeSelectionRectFromPoints,
} from '../utils/normalizeSelectionRect';
import resolveAnnotateHitFromKonvaNode from '../utils/resolveAnnotateHitFromKonvaNode';
import useStore from './useStore';

const isTransformerNode = (node) => {
  let current = node;
  while (current) {
    if (current.id?.() === NODES_TRANSFORMER_ID) {
      return true;
    }
    if (current.getClassName?.() === 'Transformer') {
      return true;
    }
    current = current.getParent?.();
  }
  return false;
};

const useMarqueeSelection = (canvasRef) => {
  const {
    dispatch,
    annotations,
    designLayer,
    editorPreferences,
    textIdOfEditableContent,
  } = useStore();

  const [marqueeRect, setMarqueeRect] = useState(null);
  const dragSessionRef = useRef(null);
  const suppressCanvasClearRef = useRef(false);
  const annotationsRef = useRef(annotations);
  const designLayerRef = useRef(designLayer);

  annotationsRef.current = annotations;
  designLayerRef.current = designLayer;

  const isSelectMode = isCanvasSelectMode(editorPreferences);
  const isPanMode = isCanvasPanMode(editorPreferences);

  useEffect(() => {
    const stage = canvasRef.current;
    if (
      !stage ||
      !isSelectMode ||
      isPanMode ||
      textIdOfEditableContent
    ) {
      setMarqueeRect(null);
      dragSessionRef.current = null;
      return undefined;
    }

    const finishDrag = (evt) => {
      const session = dragSessionRef.current;
      if (!session) {
        return;
      }

      dragSessionRef.current = null;
      setMarqueeRect(null);

      stage.setPointersPositions(evt);
      const pointer = stage.getPointerPosition();
      if (!pointer) {
        return;
      }

      const rect = normalizeSelectionRectFromPoints(
        session.startX,
        session.startY,
        pointer.x,
        pointer.y,
      );

      if (
        rect.width < MIN_MARQUEE_SELECTION_SIZE &&
        rect.height < MIN_MARQUEE_SELECTION_SIZE
      ) {
        return;
      }

      const annotationIds = getAnnotationIdsInMarqueeRect(
        designLayerRef.current,
        annotationsRef.current,
        rect,
      );

      suppressCanvasClearRef.current = true;

      dispatch({
        type: SELECT_ANNOTATIONS_IN_RECT,
        payload: {
          annotationIds,
          additive: false,
        },
      });
    };

    const onPointerMove = (evt) => {
      const session = dragSessionRef.current;
      if (!session) {
        return;
      }

      stage.setPointersPositions(evt);
      const pointer = stage.getPointerPosition();
      if (!pointer) {
        return;
      }

      setMarqueeRect(
        normalizeSelectionRectFromPoints(
          session.startX,
          session.startY,
          pointer.x,
          pointer.y,
        ),
      );
    };

    const onPointerDown = (evt) => {
      if (!evt.shiftKey || evt.button !== 0) {
        return;
      }

      stage.setPointersPositions(evt);
      const pointer = stage.getPointerPosition();
      if (!pointer) {
        return;
      }

      const target = stage.getIntersection(pointer);
      if (target) {
        if (isTransformerNode(target)) {
          return;
        }

        const hit = resolveAnnotateHitFromKonvaNode(
          target,
          annotationsRef.current,
        );
        if (hit?.annotationId) {
          return;
        }
      }

      evt.preventDefault();

      dragSessionRef.current = {
        startX: pointer.x,
        startY: pointer.y,
      };
      setMarqueeRect({
        x: pointer.x,
        y: pointer.y,
        width: 0,
        height: 0,
      });
    };

    const container = stage.container();
    container.addEventListener('mousedown', onPointerDown);
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', finishDrag);
    window.addEventListener('touchend', finishDrag);

    return () => {
      container.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', finishDrag);
      window.removeEventListener('touchend', finishDrag);
      dragSessionRef.current = null;
      setMarqueeRect(null);
    };
  }, [
    canvasRef,
    dispatch,
    isPanMode,
    isSelectMode,
    textIdOfEditableContent,
  ]);

  return {
    marqueeRect,
    isMarqueeActive: Boolean(marqueeRect),
    suppressCanvasClearRef,
  };
};

export default useMarqueeSelection;
